/**
 * ctaStyles — the ONE premium CTA + lit-panel language for the floating overlays
 * (BL-188 / S177). The Deploy button (`PreRunScreenStyles.ts:271-285`) is fully
 * premium — lit gradient ground, edge-glow, a restrained foil sweep — while the
 * end-screen / dialog / pause buttons were bare 1px hover-invert boxes (UI_AUDIT
 * §A "CTA vocabulary splits in two"). This module gives them ALL one vocabulary,
 * derived from the SAME tokens the canvas material uses (`UI_PANEL_MATERIAL`,
 * `UI_EASE`/`UI_DURATION`), so the overlays read as the same lit hardware as the
 * rest of the app — ELEVATED, not a new theme.
 *
 * WHY CSS, not `UIMaterial.drawLitPanel`: the lit-panel canvas primitive paints
 * onto the chrome canvas BEHIND the HUD frames; the dialogs/end-screens float
 * over the live arena where a canvas-behind alignment is too fragile (see
 * `main.ts` — "the dialog/overlay floats above the arena and uses the sanctioned
 * CSS lit-edge degrade rung instead"). So this is the CSS lit-edge rung, built
 * from the material's TOKEN VALUES (edge colour / corner radius / inner gradient
 * / state intensity) so it is the same material expressed in CSS, not a lookalike.
 *
 * D-225: every colour resolves from `COLORS` / `UI_PANEL_MATERIAL` (whose inner
 * gradient is itself the central `PANEL` tokens) —
 * NO new inline hex (alpha tints use `rgba()` / the `${COLOR}aa` suffix, which
 * the inline-hex ratchet does not count). Motion is `UI_EASE`/`UI_DURATION` and
 * is gated by an `@media (prefers-reduced-motion: reduce)` block every consumer
 * inherits, so the foil sweep / hover lift never run for a reduced-motion user.
 */
import { COLORS } from "../core/constants.ts";
import {
  UI_DURATION,
  UI_EASE,
  UI_FOIL,
  UI_FONT_STACK,
  UI_PANEL_MATERIAL,
  roleFontSize,
} from "./uiTokens.ts";

/** Convert an `#rrggbb` to an `rgba(r,g,b,a)` string. The hex is ALWAYS a
 *  central palette constant (never a literal here), so this introduces no new
 *  inline hex — it just lets a token carry a structural alpha. */
export function rgba(hex: string, alpha: number): string {
  const c = hex.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * The shared lit-panel CSS for a floating overlay PANEL (dialog / pause card).
 * Mirrors `UI_PANEL_MATERIAL`: the inner-gradient lit surface, the rounded
 * corner, and an edge-glow stroke whose opacity tracks `edgeOpacity`. `colour`
 * defaults to the material's green identity; pass a role colour for a tinted
 * panel. The result is a string of CSS declarations (no selector) for an inline
 * style or a rule body.
 */
export function litPanelCss(colour: string = UI_PANEL_MATERIAL.edgeColor): string {
  const [innerTop, innerBottom] = UI_PANEL_MATERIAL.innerGradient;
  const edge = UI_PANEL_MATERIAL.edgeOpacity;
  return [
    `background: linear-gradient(180deg, ${innerTop}, ${innerBottom})`,
    `border: 1px solid ${rgba(colour, 0.55)}`,
    `border-radius: ${UI_PANEL_MATERIAL.cornerRadius * 1.5}px`,
    // edge-glow: a tight outer filament + a faint inner lit fill (the §6.1
    // hot-edge read, in CSS) + a 1px rim — all from the role colour.
    `box-shadow: 0 0 ${UI_PANEL_MATERIAL.edgeRadius * 2}px ${rgba(colour, edge * 0.7)}, ` +
      `0 0 0 1px ${rgba(colour, edge * 0.5)}, ` +
      `inset 0 0 ${UI_PANEL_MATERIAL.edgeRadius * 1.6}px ${rgba(colour, edge * 0.18)}`,
  ].join("; ");
}

/**
 * The ONE premium CTA stylesheet body (BL-188). Emits the rules for a button
 * family scoped under `scope` (an id/class selector), with `btnClass` the base
 * button class. Two ranks share one material:
 *   • `.${btnClass}` — the SECONDARY rank: a ghost lit-panel (faint ground,
 *     dim edge) that brightens + lifts on hover/active. The de-emphasised
 *     supporting actions.
 *   • `.${btnClass}.${primaryClass}` — the PRIMARY rank: a solid lit gradient
 *     ground (the Deploy vocabulary) with a stronger edge-glow and the ONE
 *     restrained foil sweep (a `.cta-foil` child element), so exactly one action
 *     leads. Pulse/foil gate on reduced-motion via the trailing @media block.
 *
 * `primaryColour` is the dominant primary's role colour (e.g. SENTINEL_GREEN on
 * victory). Secondaries keep their own per-button border/colour set by the
 * caller; this only provides the shared MATERIAL + motion. The 44px touch floor
 * is included (`pointer:coarse`).
 */
export function ctaSystemCss(opts: {
  scope: string;
  btnClass: string;
  primaryClass: string;
  primaryColour?: string;
}): string {
  const { scope, btnClass, primaryClass } = opts;
  const primary = opts.primaryColour ?? COLORS.SENTINEL_GREEN;
  // F-13 — see the @keyframes block below. One name per consumer, derived from
  // the caller's own primary class, so two sheets can never claim one name.
  const pulseName = `cta-pulse-${primaryClass.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const [innerTop, innerBottom] = UI_PANEL_MATERIAL.innerGradient;
  const b = `${scope} .${btnClass}`;
  const p = `${scope} .${btnClass}.${primaryClass}`;
  return `
    /* ── BL-188 unified premium CTA — secondary (ghost lit-panel) rank ── */
    ${b} {
      position: relative;
      overflow: hidden;
      font-family: ${UI_FONT_STACK};
      font-size: ${roleFontSize("label")};
      letter-spacing: 0.14em;
      text-transform: uppercase;
      padding: 11px 22px;
      border-radius: ${UI_PANEL_MATERIAL.cornerRadius}px;
      cursor: pointer;
      background: linear-gradient(180deg, ${rgba(innerTop, 0.55)}, ${rgba(innerBottom, 0.55)});
      border: 1px solid ${rgba(COLORS.SENTINEL_GREEN, 0.45)};
      color: ${COLORS.SENTINEL_GREEN};
      box-shadow: inset 0 0 14px ${rgba(COLORS.SENTINEL_GREEN, 0.08)};
      transition: transform ${UI_DURATION.hover}ms ${UI_EASE.settle},
                  box-shadow ${UI_DURATION.hover}ms ${UI_EASE.settle},
                  border-color ${UI_DURATION.hover}ms ${UI_EASE.settle};
    }
    ${b}:hover, ${b}:focus-visible {
      transform: translateY(-1px);
      border-color: ${rgba(COLORS.SENTINEL_GREEN, 0.85)};
      box-shadow: 0 0 18px ${rgba(COLORS.SENTINEL_GREEN, 0.35)},
                  inset 0 0 16px ${rgba(COLORS.SENTINEL_GREEN, 0.14)};
      outline: none;
    }
    ${b}:active { transform: translateY(0); }

    /* ── PRIMARY rank — the ONE dominant action (Deploy vocabulary) ── */
    ${p} {
      color: ${COLORS.BG_PRIMARY};
      border-color: ${rgba(primary, 0.9)};
      background: linear-gradient(180deg, ${rgba(primary, 0.95)}, ${primary} 60%, ${rgba(primary, 0.7)});
      box-shadow: 0 0 26px ${rgba(primary, 0.55)}, 0 0 0 1px ${rgba(primary, 0.35)};
      font-weight: bold;
      animation: ${pulseName} 2.4s ${UI_EASE.glide} infinite;
    }
    ${p}:hover, ${p}:focus-visible {
      transform: translateY(-1px);
      box-shadow: 0 0 36px ${rgba(primary, 0.8)}, 0 0 0 1px ${rgba(primary, 0.55)};
    }
    /* the ONE restrained foil sweep — only on the primary, like Deploy */
    ${p} .cta-foil {
      position: absolute; inset: 0; pointer-events: none;
      background: linear-gradient(110deg, transparent 40%, ${rgba(COLORS.TEXT_GLITCH, 0.42)} 50%, transparent 60%);
      animation: cta-foil-sweep ${UI_FOIL.foilSweepMs}ms linear infinite;
    }
    /* F-13 — the keyframe name is PER CONSUMER, not the global "cta-pulse".
       Four sheets call ctaSystemCss (modalShell, GameOverScreen,
       VictoryScreen, SellConfirmDialog) and each baked its own colour into one
       shared name; CSS hands the whole document the LAST declaration, and
       SellConfirmDialog injects eagerly from its constructor (main.ts:464), so
       its ORACLE_RED always won. Measured on build 89: the settings APPLY
       button's computed box-shadow was rgba(232,154,184,.584) — the
       DESTRUCTIVE hue on the affirmative primary, on every primary CTA in the
       game. AC-3 stayed green throughout because it checks the CTA's color,
       which was always right; the defect lived in the glow, which is what
       carries at a glance. Deriving the name from primaryClass makes the
       collision structurally impossible rather than merely fixed. */
    @keyframes ${pulseName} {
      0%, 100% { box-shadow: 0 0 22px ${rgba(primary, 0.45)}, 0 0 0 1px ${rgba(primary, 0.3)}; }
      50% { box-shadow: 0 0 38px ${rgba(primary, 0.75)}, 0 0 0 1px ${rgba(primary, 0.5)}; }
    }
    @keyframes cta-foil-sweep {
      0% { transform: translateX(-120%); }
      100% { transform: translateX(120%); }
    }

    /* 44px touch floor (BL-187/193 — pointer:coarse). */
    @media (pointer: coarse) { ${b} { min-height: 44px; } }

    /* Reduced motion: no lift, no pulse, no foil — the material stays, the
       motion stops (every overlay that uses this CTA inherits this seam). */
    @media (prefers-reduced-motion: reduce) {
      ${b}, ${p} { transition: none; animation: none; }
      ${b}:hover, ${b}:focus-visible, ${p}:hover, ${p}:focus-visible { transform: none; }
      ${p} .cta-foil { display: none; }
    }
  `;
}
